import React from "react";
import "./DashboardV1Layout.css";
import { getResizePresets } from "../../services/api/catalog/resize_presets";
import { getPassportPresets } from "../../services/api/catalog/passport_presets";
import { getDashboardTools } from "../../services/api/catalog/dashboard_tools";
import {
  PASSPORT_CATEGORY_ID,
  getPresetDisplayLabel,
} from "../../utils/passportPresetUtils";
import { RESIZE_ENTRY_CATEGORY_IDS } from "../../utils/editorEntrySession";
import { mapApiToolToCardItem, mergeDashboardToolSections, sortToolsAvailableFirst } from "../../config/dashboard/trendingToolsConfig";
import CategorySeeAllModal from "./CategorySeeAllModal";
import { getCategoryRowDescription } from "../../config/dashboard/dashboardCategoryModalConfig";
import {
  DashboardToolsSkeleton,
  DashboardCategoriesSkeleton,
} from "./DashboardCatalogSkeleton";
import crownProIcon from "../../assets/pro_icons/Crown1.png";

function withProIcon(item, categorySlug) {
  if (!item) return null;
  if (categorySlug === "ai_tools" || categorySlug === "ai_photo_studio") {
    return { ...item, proIcon: crownProIcon };
  }
  return item;
}

const DASHBOARD_CATEGORY_IDS = [3, 2, 5, 6];

const DASHBOARD_CATEGORY_STYLES = {
  2: {
    categoryClass: "v1-category-passport-photos v1-category-fixed-grid v1-category-cols-6",
    enableScroll: false,
    maxVisible: 6,
  },
  3: {
    categoryClass: "v1-category-market-places v1-category-fixed-grid v1-category-cols-6",
    enableScroll: false,
    maxVisible: 6,
  },
  5: {
    categoryClass: "v1-category-profile-pictures v1-category-fixed-grid v1-category-cols-6",
    enableScroll: false,
    maxVisible: 6,
  },
  6: {
    categoryClass: "v1-category-social-media v1-category-fixed-grid v1-category-cols-6",
    enableScroll: false,
    maxVisible: 6,
  },
};

const TOOL_SECTION_STYLES = {
  trending: {
    categoryClass: "v1-category-all-designs",
  },
  editing_tools: {
    categoryClass: "v1-category-editing-tools",
  },
  ai_tools: {
    categoryClass: "v1-category-editing-tools v1-category-ai-tools",
  },
  ai_photo_studio: {
    categoryClass: "v1-category-editing-tools v1-category-ai-photo-studio",
  },
  document_tools: {
    categoryClass: "v1-category-editing-tools v1-category-document-tools",
  },
  image_conversion: {
    categoryClass: "v1-category-editing-tools",
  },
};

function getDashboardStyle(categoryId) {
  return (
    DASHBOARD_CATEGORY_STYLES[categoryId] || {
      categoryClass: "v1-category-market-places",
      enableScroll: false,
      maxVisible: null,
    }
  );
}

function getToolSectionStyle(slug) {
  return (
    TOOL_SECTION_STYLES[slug] || {
      categoryClass: "v1-category-all-designs",
    }
  );
}

const COUNT_SUFFIX_BY_CATEGORY = { 2: "Photos Processed", 3: "Images Processed", 5: "Pics Processed", 6: "Images Processed" };

const DEFAULT_HOVER_BUTTON_LABEL = "Upload Image";

function overlayContentFor(item) {
  return {
    text: item?.overlayText || item?.hoverDescription || item?.description,
    buttonLabel: item?.overlayButtonLabel || item?.ctaText || DEFAULT_HOVER_BUTTON_LABEL,
  };
}

function DesignCategory({
  title,
  description,
  items,
  showSeeAll = true,
  categoryClass,
  enableScroll = false,
  maxVisible = null,
  onSeeAll,
  onActivateItem,
  showHoverOverlay = false,
  isPresetClickable = false,
  isToolSection = false,
}) {
  const scrollContainerRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = React.useState(false);
  const [showRightArrow, setShowRightArrow] = React.useState(false);

  const displayLimit = maxVisible != null ? maxVisible : items.length;
  const displayItems = items.slice(0, displayLimit);

  React.useEffect(() => {
    if (!enableScroll || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const checkScroll = () => {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    };

    checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      resizeObserver.disconnect();
    };
  }, [enableScroll, items.length]);

  const scroll = (direction) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const shouldShowSeeAll =
    showSeeAll && onSeeAll && (isPresetClickable || items.length > displayLimit);

  return (
    <div className={`v1-design-category ${categoryClass || ""}`.trim()}>
      <div className="v1-category-header">
        <div>
          <h3 className="v1-category-title">{title}</h3>
          <p className="v1-category-description">{description}</p>
        </div>
        {shouldShowSeeAll && (
          <button type="button" className="v1-see-all-link" onClick={onSeeAll}>
            See all
          </button>
        )}
      </div>
      <div className={`v1-category-row${enableScroll ? " v1-category-row--scrollable" : ""}`}>
        {enableScroll && (
          <>
            <div className={`v1-scroll-arrow left ${!showLeftArrow ? "hidden" : ""}`} onClick={() => scroll("left")}>
              ←
            </div>
            <div className={`v1-scroll-arrow right ${!showRightArrow ? "hidden" : ""}`} onClick={() => scroll("right")}>
              →
            </div>
          </>
        )}
        <div className="v1-category-grid" ref={enableScroll ? scrollContainerRef : null}>
          {displayItems.map((item, index) => {
            const overlayContent = overlayContentFor(item);
            const isTextCard = item.cardStyle === "text";
            const isClickable = isToolSection
              ? Boolean(item.clickable) && !item.comingSoon
              : isPresetClickable && item.preset;
            const activateItem = () => onActivateItem?.(item);
            return (
              <div
                key={`${item.toolKey || item.label}-${index}`}
                className={`v1-design-item${isClickable ? " v1-design-item--clickable" : ""}${isTextCard ? " v1-design-item--text" : ""}${item.comingSoon ? " v1-design-item--soon" : ""}`}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? activateItem : undefined}
                onKeyDown={
                  isClickable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          activateItem();
                        }
                      }
                    : undefined
                }
              >
                {item.comingSoon ? (
                  <span className="v1-coming-soon-tag">soon</span>
                ) : null}
                {!isTextCard && (
                  <div className="v1-design-item-image">
                    {item.image ? <img src={item.image} alt={item.label} /> : null}
                  </div>
                )}
                {isTextCard && item.icon ? (
                  <div
                    className="v1-design-item-app-icon"
                    style={{
                      WebkitMaskImage: `url(${item.icon})`,
                      maskImage: `url(${item.icon})`,
                    }}
                    aria-hidden="true"
                  />
                ) : null}
                {!isTextCard && item.icon ? (
                  <div className="v1-design-item-icon">
                    <img src={item.icon} alt={item.label} />
                  </div>
                ) : null}
                {!isTextCard && (
                  <p className="v1-design-item-count">{item.count || ""}</p>
                )}
                {!isTextCard && item.resolution ? (
                  <p className="v1-design-item-resolution">{item.resolution}</p>
                ) : null}
                <div className="v1-design-item-footer">
                  <div className="v1-design-item-label-wrap">
                    <p className="v1-design-item-label">
                      <span className="v1-design-item-label-text">{item.label}</span>
                      {item.proIcon ? (
                        <img
                          src={item.proIcon}
                          alt=""
                          className="v1-design-item-pro__icon"
                          title="Pro"
                        />
                      ) : null}
                    </p>
                    {isTextCard && item.resolution ? (
                      <p className="v1-design-item-text-subtitle">{item.resolution}</p>
                    ) : null}
                  </div>
                  {!isTextCard ? (
                    <div className="v1-design-item-arrow">→</div>
                  ) : null}
                </div>
                {showHoverOverlay && (
                  <div
                    className="v1-design-item-hover-overlay"
                    onClick={
                      isClickable
                        ? (e) => {
                            e.stopPropagation();
                            activateItem();
                          }
                        : undefined
                    }
                  >
                    <p className="v1-design-item-hover-text">{overlayContent.text}</p>
                    <button
                      type="button"
                      className="v1-design-item-upload-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isClickable) activateItem();
                      }}
                    >
                      {overlayContent.buttonLabel}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatProcessedCount(processedCount, categoryId) {
  if (processedCount == null || processedCount === 0) return "";
  const suffix = COUNT_SUFFIX_BY_CATEGORY[categoryId] || "Processed";
  return `${Number(processedCount).toLocaleString()} ${suffix}`;
}

function mapPresetToCardItem(p, cat) {
  return {
    preset: p,
    categoryId: cat.category_id,
    image: p.card_image_url || null,
    icon: p.icon_url || "",
    label: p.name,
    resolution: getPresetDisplayLabel(p, cat.default_display_label),
    hoverDescription: p.hover_description,
    count: formatProcessedCount(p.processed_count, cat.category_id),
  };
}

const DashboardV1Layout = ({
  onPassportPresetSelect,
  onResizeEntryPresetSelect,
  onTrendingToolSelect,
  onEditingToolSelect,
  onSampleClick,
}) => {
  const [apiCategories, setApiCategories] = React.useState(null);
  const [categoriesLoading, setCategoriesLoading] = React.useState(true);
  const [toolSections, setToolSections] = React.useState([]);
  const [toolsLoading, setToolsLoading] = React.useState(true);
  const [seeAllCategory, setSeeAllCategory] = React.useState(null);
  const [seeAllPresets, setSeeAllPresets] = React.useState([]);
  const [seeAllPresetsLoading, setSeeAllPresetsLoading] = React.useState(false);
  const seeAllFetchRef = React.useRef(0);
  const hasCategoriesRef = React.useRef(false);
  const hasToolsRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!hasCategoriesRef.current) setCategoriesLoading(true);
    Promise.allSettled([
      getResizePresets({
        categoryIds: DASHBOARD_CATEGORY_IDS.filter((id) => id !== PASSPORT_CATEGORY_ID),
        dashboardOnly: true,
      }),
      getPassportPresets({ dashboardOnly: true }),
    ])
      .then(([resizeResult, passportResult]) => {
        if (cancelled) return;
        const resizeRes =
          resizeResult.status === "fulfilled" ? resizeResult.value : null;
        const passportRes =
          passportResult.status === "fulfilled" ? passportResult.value : null;
        const resizeCats =
          resizeRes?.success && Array.isArray(resizeRes.data) ? resizeRes.data : [];
        const passportCats =
          passportRes?.success && Array.isArray(passportRes.data)
            ? passportRes.data
            : [];
        const merged = [...resizeCats, ...passportCats];
        setApiCategories(merged.length ? merged : null);
        hasCategoriesRef.current = true;
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    if (!hasToolsRef.current) setToolsLoading(true);
    getDashboardTools({ dashboardOnly: true })
      .then((res) => {
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setToolSections(mergeDashboardToolSections(res.data));
          hasToolsRef.current = true;
        } else if (!cancelled) {
          setToolSections([]);
          hasToolsRef.current = true;
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToolSections([]);
          hasToolsRef.current = true;
        }
      })
      .finally(() => {
        if (!cancelled) setToolsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const dashboardSections = React.useMemo(() => {
    if (!apiCategories?.length) return [];
    return apiCategories
      .filter(
        (c) =>
          DASHBOARD_CATEGORY_IDS.includes(c.category_id) &&
          Array.isArray(c.presets) &&
          c.presets.length > 0
      )
      .sort((a, b) => DASHBOARD_CATEGORY_IDS.indexOf(a.category_id) - DASHBOARD_CATEGORY_IDS.indexOf(b.category_id));
  }, [apiCategories]);

  const isPresetEntryCategory = (categoryId) =>
    categoryId === PASSPORT_CATEGORY_ID ||
    RESIZE_ENTRY_CATEGORY_IDS.includes(categoryId);

  const handleToolActivate = (item) => {
    if (!item?.clickable || !item?.clientTool) return;
    const slug = item.categorySlug;
    if (
      slug === "editing_tools" ||
      slug === "ai_tools" ||
      slug === "ai_photo_studio" ||
      slug === "document_tools" ||
      slug === "image_conversion"
    ) {
      if (typeof onEditingToolSelect === "function") {
        onEditingToolSelect(item.clientTool);
      }
      return;
    }
    if (typeof onTrendingToolSelect === "function") {
      onTrendingToolSelect(item.clientTool);
    }
  };

  const handleCardActivate = (item) => {
    if (
      item.categoryId === PASSPORT_CATEGORY_ID &&
      item.preset &&
      typeof onPassportPresetSelect === "function"
    ) {
      onPassportPresetSelect(item.preset);
      return;
    }
    if (
      RESIZE_ENTRY_CATEGORY_IDS.includes(item.categoryId) &&
      item.preset &&
      typeof onResizeEntryPresetSelect === "function"
    ) {
      onResizeEntryPresetSelect(item.preset, item.categoryId);
    }
  };

  const handleSeeAllOpen = (category) => {
    if (!category?.category_id) return;
    const fetchId = ++seeAllFetchRef.current;
    setSeeAllCategory(category);
    setSeeAllPresets([]);
    setSeeAllPresetsLoading(true);

    const categoryId = category.category_id;
    const fetchPresets =
      categoryId === PASSPORT_CATEGORY_ID
        ? getPassportPresets()
        : getResizePresets({ categoryIds: [categoryId] });

    fetchPresets
      .then((res) => {
        if (fetchId !== seeAllFetchRef.current) return;
        if (res?.success && Array.isArray(res.data)) {
          const match = res.data.find((c) => c.category_id === categoryId);
          setSeeAllPresets(match?.presets ?? res.data[0]?.presets ?? []);
        } else {
          setSeeAllPresets([]);
        }
      })
      .catch(() => {
        if (fetchId !== seeAllFetchRef.current) return;
        setSeeAllPresets([]);
      })
      .finally(() => {
        if (fetchId !== seeAllFetchRef.current) return;
        setSeeAllPresetsLoading(false);
      });
  };

  const handleSeeAllClose = () => {
    seeAllFetchRef.current += 1;
    setSeeAllCategory(null);
    setSeeAllPresets([]);
    setSeeAllPresetsLoading(false);
  };

  const handleSeeAllUpload = (preset, categoryId) => {
    handleCardActivate({ preset, categoryId });
  };


  return (
    <div className="v1-dashboard-content">
      {toolsLoading && toolSections.length === 0 ? (
        <DashboardToolsSkeleton />
      ) : (
        toolSections.map((section) => {
          const { categoryClass } = getToolSectionStyle(section.slug);
          const tools = sortToolsAvailableFirst(
            (section.tools || [])
              .map((t) => withProIcon(mapApiToolToCardItem(t, section.slug), section.slug))
              .filter(Boolean)
          );
          return (
            <DesignCategory
              key={section.category_id || section.slug}
              title={section.name}
              description={section.description || ""}
              categoryClass={categoryClass}
              items={tools}
              showSeeAll={false}
              isToolSection
              onActivateItem={handleToolActivate}
            />
          );
        })
      )}

      {categoriesLoading && !apiCategories ? (
        <DashboardCategoriesSkeleton />
      ) : dashboardSections.length > 0 ? (
        dashboardSections.map((cat) => {
          const { categoryClass, enableScroll, maxVisible } = getDashboardStyle(cat.category_id);
          const isPresetClickable = isPresetEntryCategory(cat.category_id);
          const presets = cat.presets || [];
          return (
            <DesignCategory
              key={cat.category_id}
              title={cat.category_name}
              description={getCategoryRowDescription(cat)}
              categoryClass={categoryClass}
              enableScroll={enableScroll}
              maxVisible={maxVisible}
              isPresetClickable={isPresetClickable}
              items={presets.map((p) => mapPresetToCardItem(p, cat))}
              showHoverOverlay={true}
              showSeeAll={isPresetClickable}
              onSeeAll={() => handleSeeAllOpen(cat)}
              onActivateItem={handleCardActivate}
            />
          );
        })
      ) : (
        <div className="v1-dashboard-content-loading">
          <p>Could not load design categories. Please try again later.</p>
        </div>
      )}

      <CategorySeeAllModal
        category={seeAllCategory}
        presets={seeAllPresets}
        isOpen={Boolean(seeAllCategory)}
        loading={seeAllPresetsLoading}
        onClose={handleSeeAllClose}
        onUpload={handleSeeAllUpload}
        onSampleClick={
          onSampleClick
            ? (sample, options) => {
                onSampleClick(sample, options);
                handleSeeAllClose();
              }
            : undefined
        }
      />
    </div>
  );
};

export default DashboardV1Layout;
